import { Subject } from 'rxjs/Subject';
import { first } from 'rxjs/operators/first';
import IframeHandler from './IframeHandler';
import { Apps } from './constants';
import { CoreMessages } from './features';
import IntegratedAppOption from './IntegratedAppOptions';
import { filter } from 'rxjs/operators/filter';
export default class IntegratedAppEmbedder {
  constructor(app, portalId, baseUrl, getIframeHeight, impactCode = '', iframeOptions = {}, integrationName = 'WordPress') {
    this.optionsManager = new IntegratedAppOption();
    this.observable = new Subject();
    this.messageStack = [];
    this.ready = false;
    this.impactCodeBaseUrl = 'https://hubspot.sjv.io/';
    this.app = app;
    this.portalId = portalId;
    this.baseUrl = baseUrl;
    this.getIframeHeight = getIframeHeight;
    this.impactCode = impactCode;
    this.iframeOptions = iframeOptions;
    this.integrationName = integrationName;
    const route = this.resolveRoute();
    console.info('HubSpot plugin - IntegratedAppEmbedder initializing for:', JSON.stringify({
      app,
      baseUrl,
      route
    }, null, 2));
    this.iframeHandler = new IframeHandler(app, route, this.getIframeHeight, app !== Apps.Background, this.iframeOptions);
    window.addEventListener('message', this.onMessageReceived.bind(this));
    this.flushExistingOptions();
    this.listenForFrameReload();
    this.listenForFrameRedirect();
    this.listenForSignupHandshakeComplete();
  }
  resolveRoute() {
    const integrationName = this.integrationName.toLocaleLowerCase();
    switch (this.app) {
      case 'integrated-plugin-app':
        {
          if (this.impactCode) {
            return `${this.impactCodeBaseUrl}${this.impactCode}?u=${encodeURIComponent(`${this.baseUrl}/integrated-plugin-ui/${this.portalId}`)}&integration=${integrationName}`;
          }
          return `${this.baseUrl}/integrated-plugin-ui/${this.portalId}?integration=${integrationName}`;
        }
      case 'integrated-form-app':
        return `${this.baseUrl}/integrated-forms-ui/${this.portalId}/dashboard?integration=${integrationName}`;
      case 'integrated-livechat-app':
        return `${this.baseUrl}/integrated-livechat-ui/${this.portalId}?integration=${integrationName}`;
      case 'integrated-plugin-proxy':
        return `${this.baseUrl}/integrated-plugin-proxy/${this.portalId}?integration=${integrationName}`;
      case 'integrated-google-workspace-app':
        return `${this.baseUrl}/integrated-google-workspace-app-ui/${this.portalId}`;
      default:
        return this.baseUrl;
    }
  }
  onMessageReceived(event) {
    const iframe = this.iframeHandler.getIframe();
    if (iframe && event.source === iframe.contentWindow) {
      this.observable.next(event.data);
    }
  }
  listenForFrameReload() {
    this.observable.pipe(filter(message => message.key === CoreMessages.ReloadParentFrame)).subscribe(() => {
      window.location.reload();
    });
  }
  listenForFrameRedirect() {
    this.observable.pipe(filter(message => message.key === CoreMessages.RedirectParentFrame)).subscribe(message => {
      window.location.href = message.payload;
    });
  }
  listenForSignupHandshakeComplete() {
    this.observable.pipe(filter(message => message.key === CoreMessages.SignupHandshakeComplete || message.key === CoreMessages.HandshakeReceive)).subscribe(() => {
      this.iframeHandler.removeLoader();
    });
  }
  flushExistingOptions() {
    this.observable.pipe(first(value => value.key === CoreMessages.HandshakeReceive)).subscribe(() => {
      this.ready = true;
      [...this.optionsManager.options, ...this.messageStack].forEach(option => {
        const iframe = this.iframeHandler.getIframe();
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(option, '*');
        }
      });
      this.messageStack = [];
      this.optionsManager.flush();
    });
  }
  setOptions(optionsManager) {
    this.optionsManager = optionsManager;
    return this;
  }
  attachTo(el, showLoading = true) {
    if (showLoading) {
      const iframe = this.iframeHandler.withLoadingSpinner();
      el.append(iframe);
    } else {
      el.append(this.iframeHandler.getIframe());
    }
    return this;
  }
  subscribe(callback) {
    return this.observable.subscribe(callback);
  }
  postMessage(option) {
    const iframe = this.iframeHandler.getIframe();
    if (!this.ready) {
      this.messageStack.push(option);
    } else if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(option, '*');
    }
  }
  resizeIframe({
    height = 0,
    width = 0
  }) {
    const iframe = this.iframeHandler.getIframe();
    iframe.style.minHeight = `${height}px`;
    iframe.style.width = `${width}px`;
  }
  /**
   * Posts an asynchronous message to an iframe and awaits a matching response.
   *
   * The function sends a message to an iframe's contentWindow and listens
   * for a response that matches the same message key and a generated requestId.
   * If the iframe isn't found, the promise is rejected.
   *
   * @param {Message<K>} message - The message object to send, where K is the message key type.
   * @returns {Promise<any>} - A promise that resolves with the payload of the received response message.
   * @throws {Error} - Throws an error if the iframe is not found.
   */
  postAsyncMessage(message) {
    const iframe = this.iframeHandler.getIframe();
    return new Promise((res, rej) => {
      const requestId = Date.now();
      this.observable.pipe(first(value => value.requestId === requestId && value.key === message.key)).subscribe(({
        payload
      }) => {
        res(payload);
      });
      if (!this.ready) {
        this.messageStack.push(Object.assign({}, message, {
          requestId
        }));
      } else if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(Object.assign({}, message, {
          requestId
        }), '*');
      } else {
        rej(new Error('Iframe not found'));
      }
    });
  }

  /**
   * Sends a 'StartApp' message which is used to signal that the required
   * data has been sent to the integrated app that is being embedded.
   */
  postStartAppMessage() {
    console.info('HubSpot plugin - IntegratedAppEmbedder send StartApp message for:', this.app);
    this.postMessage({
      key: CoreMessages.StartApp,
      payload: this.integrationName
    });
  }
}